// Counter Intelligence — natural-language single-item price change helpers.
//
// The classifier extracts the item name and target price from chat text; this
// module turns those extracted entities into a preview and finds the matching
// catalog item. It is pure (no React / no I/O) so it is unit-testable.

import { getCandidates, scoreCandidacy, jaccardSimilarity } from './priceUpdateMatching';

const CURRENCY_RE = /\$?\s*\d{1,6}(?:\.\d{1,2})?/;

export function extractPriceChangeRequest(classification) {
  if (!classification || typeof classification !== 'object') return null;
  const name = typeof classification.extracted_item_name === 'string'
    ? classification.extracted_item_name.trim()
    : '';
  const price = Number.isFinite(Number(classification.extracted_price))
    ? Number(classification.extracted_price)
    : null;
  if (!name || price === null || price < 0) return null;
  return { itemName: name, newPrice: price };
}

export function findMatchingCatalogItem(name, catalog) {
  const items = Array.isArray(catalog) ? catalog : [];
  if (!name || !items.length) return { match: null, candidates: [] };

  const candidates = getCandidates({ name, vendor: '', unit: '' }, items, 5);
  if (!candidates.length) return { match: null, candidates: [] };

  const top = candidates[0];
  // For a single-item price change we rarely know vendor/unit, so we require a
  // strong name-only match. scoreCandidacy's default vendor/unit signals would
  // otherwise inflate scores for unrelated items.
  const nameScore = jaccardSimilarity(name, top.description);
  const MATCH_THRESHOLD = 0.4;
  if (nameScore >= MATCH_THRESHOLD) {
    return { match: top, candidates: candidates.slice(0, 3) };
  }
  return { match: null, candidates: candidates.slice(0, 3) };
}

export function buildPriceChangePreview(item, newPrice) {
  if (!item || newPrice == null || !Number.isFinite(newPrice)) return null;
  const oldPrice = item.case_price != null ? parseFloat(item.case_price) : null;
  const delta = oldPrice != null && Number.isFinite(oldPrice) ? newPrice - oldPrice : null;
  const pct = delta != null && oldPrice ? (delta / oldPrice) * 100 : null;
  return {
    itemId: item.id,
    itemName: item.description || 'this item',
    oldPrice,
    newPrice,
    delta,
    pct,
  };
}

function fmtMoney(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : '—';
}

export function formatPriceChangeSummary(preview) {
  if (!preview) return '';
  const { itemName, oldPrice, newPrice, pct } = preview;
  const priceLine = oldPrice != null
    ? `${fmtMoney(oldPrice)} → ${fmtMoney(newPrice)}`
    : fmtMoney(newPrice);
  const pctPart = pct != null ? ` (${pct > 0 ? '+' : ''}${pct.toFixed(1)}%)` : '';
  return `${itemName}: ${priceLine}${pctPart}`;
}

export function priceChangeDateChips() {
  return [
    { key: 'pc_date_today', label: 'Today' },
    { key: 'pc_date_past', label: 'Set a past date' },
  ];
}

// Lightweight date parser for free-text past dates. Returns an ISO day string
// (YYYY-MM-DD) or null if it cannot resolve.
export function parsePastDate(text, now = new Date()) {
  const t = String(text || '').trim().toLowerCase();
  if (!t) return null;

  // Explicit ISO-ish date.
  const iso = t.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (iso) {
    const [_, y, m, d] = iso.map(Number);
    const dt = new Date(y, m - 1, d);
    if (!Number.isNaN(dt.getTime()) && dt <= now) return formatIsoDay(dt);
  }

  // Relative day keywords.
  if (/\byesterday\b/.test(t)) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    return formatIsoDay(d);
  }
  if (/\blast week\b/.test(t)) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
    return formatIsoDay(d);
  }

  // "last Monday", "this past Tuesday", etc.
  const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayMatch = t.match(/(?:last|past)\s+(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/);
  if (dayMatch) {
    const targetDay = weekdays.indexOf(dayMatch[1]);
    const today = now.getDay();
    let daysBack = (today - targetDay + 7) % 7;
    if (daysBack === 0) daysBack = 7;
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysBack);
    return formatIsoDay(d);
  }

  return null;
}

function formatIsoDay(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
